import { useTranslation, useIsRTL } from '@spcc/core';
import { Link } from '@spcc/core';
import {
  ArrowUpRight,
  ShoppingCart,
  Eye,
  RefreshCw,
  Trash2,
  MoreHorizontal,
  Package,
  Truck,
  Factory,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TenantCell,
  cn,
} from '@spcc/uikit';
import type { PackagingType } from '../../../../domain/types';
import type { SalesOrderCardProps } from '../types';
import { statusConfig, getProductColorStyles } from '../constants';
import { calculateUtilization, getSalesOrderLineLabel } from '../utils';

export function SalesOrderCard({
  salesOrder,
  showTenantColumn,
  onView,
  onReorder,
  onCancel,
}: SalesOrderCardProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useIsRTL();
  const isArabic = i18n.language === 'ar'; // For Arabic-specific content

  const config = statusConfig[salesOrder.status];
  const utilization = calculateUtilization(salesOrder);
  const ordersCount = salesOrder.ordersCount ?? { total: 0, pending: 0 };
  const productLines = ((salesOrder as any).products ??
    (salesOrder as any).lines ??
    []) as any[];

  return (
    <div className="glass-card rounded-2xl overflow-hidden hover:shadow-xl transition-[box-shadow,border-color] duration-300 group">
      {/* Card Header */}
      <div
        className={cn(
          `bg-gradient-to-r ${config.gradient} p-4`,
          isRTL && 'bg-gradient-to-l',
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 text-start">
            <p className="text-white/70 text-xs font-medium truncate">
              {salesOrder.orderNumber}
            </p>
            <h3 className="text-white font-bold text-sm leading-tight truncate mt-0.5">
              {isArabic ? salesOrder.name.ar : salesOrder.name.en}
            </h3>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(salesOrder)}>
                <Eye className="w-4 h-4 me-2" />
                {t('common.view')}
              </DropdownMenuItem>
              {onReorder && (
                <DropdownMenuItem onClick={() => onReorder(salesOrder)}>
                  <RefreshCw className="w-4 h-4 me-2" />
                  {t('salesOrders.reorder')}
                </DropdownMenuItem>
              )}
              {onCancel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onCancel(salesOrder)}
                    variant="destructive"
                  >
                    <Trash2 className="w-4 h-4 me-2" />
                    {t('salesOrders.cancel')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* Products */}
        <div className="flex items-center gap-1 flex-wrap">
          {productLines.slice(0, 3).map((line, idx) => {
            const label = getSalesOrderLineLabel(line as any, isArabic) || '—';
            const packaging =
              ((line as any).packaging as PackagingType) || 'bags';
            const colors = getProductColorStyles(label);
            const PackageIcon = packaging === 'bulk' ? Truck : Package;

            return (
              <div
                key={idx}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium"
                style={{ backgroundColor: colors.bg, color: colors.text }}
              >
                <span className="max-w-[160px] truncate">{label}</span>
                <PackageIcon className="w-3 h-3 opacity-60" />
              </div>
            );
          })}
        </div>

        {/* Tenant (Admin Only) */}
        {showTenantColumn && salesOrder.tenant && (
          <div className="pt-1">
            <TenantCell tenant={salesOrder.tenant} />
          </div>
        )}

        {/* Factory */}
        {salesOrder.factoryName && (
          <div className={cn('flex items-center gap-2 pt-1')}>
            <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Factory className="w-3 h-3 text-slate-500" />
            </div>
            <span className="text-xs text-muted-foreground truncate">
              {salesOrder.factoryName}
            </span>
          </div>
        )}

        {/* Consumption */}
        <div className={cn('flex items-center justify-between')}>
          <div>
            <p className="text-xs text-muted-foreground">
              {t('salesOrders.consumption')}
            </p>
            <p
              className={cn(
                'text-lg font-bold',
                utilization >= 90
                  ? 'text-red-600'
                  : utilization >= 70
                    ? 'text-amber-600'
                    : 'text-emerald-600',
              )}
            >
              {utilization}%
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full',
              utilization >= 90
                ? 'bg-red-500'
                : utilization >= 70
                  ? 'bg-amber-500'
                  : 'bg-emerald-500',
            )}
            style={{ width: `${utilization}%` }}
          />
        </div>

        {/* Orders */}
        <Link
          to="/orders"
          search={{ salesOrderId: salesOrder.id }}
          className="flex items-center gap-2 p-2 rounded-xl bg-purple-50 dark:bg-purple-500/10 hover:bg-purple-100 dark:hover:bg-purple-500/20 transition-colors"
        >
          <ShoppingCart className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-purple-700 flex-1">
            {ordersCount.total} {t('salesOrders.orders')}
          </span>
          {ordersCount.pending > 0 && (
            <Badge className="bg-amber-500 text-white border-0 text-xs">
              {ordersCount.pending}
            </Badge>
          )}
          <ArrowUpRight className="w-4 h-4 text-purple-400" />
        </Link>

        {/* Actions */}
        <Button
          onClick={() => onView(salesOrder)}
          className="w-full h-9 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 border-0"
        >
          <Eye className="w-4 h-4 me-2" />
          {t('salesOrders.details')}
        </Button>
      </div>
    </div>
  );
}
